import { cancelable } from 'cancelable-promise';
import { extract, parse, stringify } from 'query-string';
import { SUPPORTED_3D_MODELS, THREE_JS_DEPS_KEYS, TRANSPARENT_IMAGE_SRC } from '../../constants/Constants';
import { AssetViewerViewMapper } from '../../helpers/AssetViewerViewMapper';
import { DomHelper } from '../../helpers/DomHelper';
import { FullscreenHelper } from '../../helpers/FullscreenHelper';
import { TranslationKeys } from '../../types/Translations.type';
import { ViewerEventType, ViewerStartLoading3dModelEvent, ViewerStartViewing3dModelEvent, ViewerStopViewing3dModelEvent, ViewerToggleFullscreenEvent } from '../../types/ViewerEvents.type';
import { mapRange } from '../../utils/math';
import { ThreeJsDAELoader as DAELoader } from './ThreeJsModelLoaders';
import { ThreeJsModelRenderer as ModelRenderer } from './ThreeJsModelRenderer';
export class ThreeJsPresentationRenderer {
    constructor(assetViewerView, presentation) {
        this.assetViewerView = assetViewerView;
        this.presentation = presentation;
        this.container = null;
        this.content = null;
        this.modelContainer = null;
        this.manifestXhr = null;
        this.manifest = null;
        this.unsupportedMessage = null;
        this.preview = null;
        this.buttonView = null;
        this.buttonExitFullscreen = null;
        this.loadingIndicator = null;
        this.errorMessage = null;
        this.loadDependenciesPromise = null;
        this.previewImage = null;
        this.previewImageError = false;
        this.isLoading = false;
        this.isFullscreen = false;
        this.isActive = false;
        this.renderModel = this.renderModel.bind(this);
        this.onPreviewClick = this.onPreviewClick.bind(this);
        this.onButtonViewClick = this.onButtonViewClick.bind(this);
        this.onButtonExitFullscreenClick = this.onButtonExitFullscreenClick.bind(this);
        this.onAssetViewerViewFocusChange = this.onAssetViewerViewFocusChange.bind(this);
        this.onAssetViewerViewFullscreenChange = this.onAssetViewerViewFullscreenChange.bind(this);
        this.onModelLoaderProgess = this.onModelLoaderProgess.bind(this);
        this.onModelLoaderLoaded = this.onModelLoaderLoaded.bind(this);
        this.onModelLoaderError = this.onModelLoaderError.bind(this);
    }
    get dimensions() {
        if (this.config.THREEJS_USE_PREVIEW_DIMENSIONS === false) {
            return null;
        }
        return this.width && this.height ? { width: this.width, height: this.height } : { width: 600, height: 400 };
    }
    get width() {
        if (this._width) {
            return this._width;
        }
    }
    get height() {
        if (this._height) {
            return this._height;
        }
    }
    set width(value) {
        this._width = value;
    }
    set height(value) {
        this._height = value;
    }
    get hasMetaData() {
        if (this.url && !this.manifest) {
            return false;
        }
        if (!this.previewUrl || this.previewImageError) {
            return true;
        }
        if (this.previewUrl && this.previewImage) {
            return true;
        }
        return false;
    }
    get previewUrl() {
        const previewUrl = this.assetViewerView.assetViewerItem.previewUrl;
        if (!previewUrl || this.previewImageError) {
            return null;
        }
        const queryParams = parse(extract(previewUrl));
        queryParams['size'] = '1600x1600';
        return `${previewUrl.split('?')[0]}?${stringify(queryParams)}`;
    }
    get url() {
        if (this.presentation.hasOwnProperty('urls') === false) {
            return null;
        }
        return this.presentation.urls[0];
    }
    get config() {
        return this.assetViewerView.config;
    }
    get translations() {
        return this.config.TRANSLATIONS;
    }
    get modelUrl() {
        return this.url.replace('manifest.json', this.manifest.model);
    }
    get modelExtension() {
        return this.manifest.model
            .split('.')
            .pop()
            .toLowerCase();
    }
    get readMoreLinkHtml() {
        return `<a href="${this.config.THREEJS_HELP_CENTRE_LEARNMORE_URL}" target="_blank">${this.translations[TranslationKeys.ThreeJsReadMore]}</a>`;
    }
    get is3dModelSupported() {
        return SUPPORTED_3D_MODELS.some(model3d => model3d.extension === this.modelExtension);
    }
    get isWebglSupported() {
        try {
            const canvas = document.createElement('canvas');
            return !!(window.WebGLRenderingContext && (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')));
        }
        catch (e) {
            return false;
        }
    }
    load() {
        this.isLoading = true;
        return Promise.all([this.loadManifest(), this.loadPreviewImage()]).then(() => {
            this.isLoading = false;
        });
    }
    loadManifest() {
        if (!this.url) {
            return Promise.resolve();
        }
        return new Promise((resolve, reject) => {
            const xhr = (this.manifestXhr = new XMLHttpRequest());
            xhr.open('GET', this.url, true);
            xhr.onload = () => {
                const status = xhr.status;
                if (status === 200) {
                    try {
                        this.manifest = JSON.parse(xhr.response);
                    }
                    catch (e) {
                        reject(e);
                    }
                    resolve(undefined);
                }
                else {
                    reject(status);
                }
            };
            xhr.send();
        });
    }
    loadPreviewImage() {
        if (!this.previewUrl) {
            return Promise.resolve();
        }
        return new Promise(resolve => {
            const image = (this.previewImage = new Image());
            image.onload = () => {
                if (image.width && image.height) {
                    this.width = image.naturalWidth;
                    this.height = image.naturalHeight;
                    resolve(undefined);
                }
                else {
                    // If we reject then we show the error presentation renderer!
                    resolve(undefined);
                }
                image.onload = null;
                image.onerror = null;
            };
            image.onerror = error => {
                this.previewImageError = true;
                image.onload = null;
                image.onerror = null;
                // If we reject then we show the error presentation renderer!
                resolve(undefined);
            };
            image.src = this.previewUrl;
        });
    }
    render(container) {
        this.container = container;
        return Promise.resolve().then(() => {
            this.content = document.createElement('div');
            this.content.className = 'sv-threejs-presentation-renderer';
            this.preview = new ThreeJsPresentationRendererPreview(this.content, this.previewUrl);
            this.modelContainer = document.createElement('div');
            this.modelContainer.className = 'sv-threejs-presentation-renderer__model-container';
            new ThreeJsPresentationRendererPreview(this.modelContainer, this.previewUrl);
            this.buttonView = new ThreeJsPresentationRendererButtonView(this.content, this.translations);
            this.errorMessage = new ThreeJsPresentationRendererErrorMessage(this.content, this.translations);
            this.loadingIndicator = new ThreeJsPresentationRendererLoadingIndicator(this.modelContainer, this.translations);
            this.buttonExitFullscreen = new ThreeJsPresentationRendererButtonExitFullscreen(this.modelContainer);
            container.appendChild(this.content);
            if (!this.url) {
                return;
            }
            if (this.isWebglSupported === false) {
                return new ThreeJsPresentationRendererUnsupportedMessage(this.content, this.translations[TranslationKeys.ThreeJsWebglNotSupported]
                    .replace('{{webGl}}', '<b>WebGL</b>')
                    .replace('{{readMoreLink}}', this.readMoreLinkHtml));
            }
            if (this.is3dModelSupported === false) {
                return new ThreeJsPresentationRendererUnsupportedMessage(this.content, this.translations[TranslationKeys.ThreeJsUnsupportedFile]
                    .replace('{{modelExtension}}', `<b>.${this.modelExtension}</b>`)
                    .replace('{{readMoreLink}}', this.readMoreLinkHtml));
            }
            this.addEventListeners();
            this.buttonView.on();
        });
    }
    addEventListeners() {
        this.preview.onclick = this.onPreviewClick;
        this.buttonView.onclick = this.onButtonViewClick;
        this.loadingIndicator.onComplete = this.renderModel;
        this.buttonExitFullscreen.onclick = this.onButtonExitFullscreenClick;
        this.assetViewerView.eventBus.on(ViewerEventType.FocusChange, this.onAssetViewerViewFocusChange);
        this.assetViewerView.eventBus.on(ViewerEventType.FullscreenChange, this.onAssetViewerViewFullscreenChange);
    }
    removeEventListeners() {
        if (this.preview) {
            this.preview.onclick = null;
        }
        if (this.buttonView) {
            this.buttonView.onclick = null;
        }
        if (this.loadingIndicator) {
            this.loadingIndicator.onComplete = null;
        }
        if (this.buttonExitFullscreen) {
            this.buttonExitFullscreen.onclick = null;
        }
        this.assetViewerView.eventBus.off(ViewerEventType.FocusChange, this.onAssetViewerViewFocusChange);
        this.assetViewerView.eventBus.off(ViewerEventType.FullscreenChange, this.onAssetViewerViewFullscreenChange);
    }
    onPreviewClick() {
        this.viewModel();
    }
    onButtonViewClick() {
        this.viewModel();
    }
    onButtonExitFullscreenClick() {
        this.assetViewerView.eventBus.dispatch(new ViewerToggleFullscreenEvent());
    }
    onModelLoaderProgess(percent) {
        this.loadingIndicator.update(mapRange(percent, 0, 100, 25, 100));
    }
    onModelLoaderLoaded(model) {
        this.model = model;
    }
    onModelLoaderError(error) {
        this.loadingIndicator.off();
        this.buttonView.on();
        this.errorMessage.on();
        this.destroyModel();
    }
    onAssetViewerViewFocusChange(event) {
        this.loadingIndicator.off();
        this.errorMessage.off();
        this.buttonView.on();
        this.destroyModel();
    }
    onAssetViewerViewFullscreenChange(event) {
        if (this.isFullscreen === true && !FullscreenHelper.isFullscreen()) {
            this.isFullscreen = null;
            this.loadingIndicator.off();
            this.destroyModel();
            this.buttonView.on();
            this.assetViewerView.eventBus.dispatch(new ViewerStopViewing3dModelEvent({
                assetViewerView: AssetViewerViewMapper.mapToPublicAssetViewerView(this.assetViewerView)
            }));
        }
    }
    viewModel() {
        this.buttonView.off();
        this.errorMessage.off();
        this.loadingIndicator.on(25);
        if (this.config.THREEJS_VIEW_FULLSCREEN === true) {
            this.config.container.parentNode.appendChild(this.modelContainer);
            FullscreenHelper.enterFullscreen(this.modelContainer);
            this.isFullscreen = true;
            this.buttonExitFullscreen.on();
        }
        else {
            this.content.appendChild(this.modelContainer);
        }
        this.loadDependencies();
        this.assetViewerView.eventBus.dispatch(new ViewerStartLoading3dModelEvent({ assetViewerView: AssetViewerViewMapper.mapToPublicAssetViewerView(this.assetViewerView) }));
    }
    loadDependencies() {
        this.loadDependenciesPromise = cancelable(DomHelper.loadScriptsInOrder([
            this.config.THREE_JS_DEPS[THREE_JS_DEPS_KEYS.THREE].src,
            this.config.THREE_JS_DEPS[THREE_JS_DEPS_KEYS.ORBIT_CONTROLS].src,
            this.config.THREE_JS_DEPS[THREE_JS_DEPS_KEYS.COLLADA_LOADER].src,
            this.config.THREE_JS_DEPS[THREE_JS_DEPS_KEYS.TGA_LOADER].src
        ]));
        this.loadDependenciesPromise.then(() => {
            this.loadModel();
        });
    }
    loadModel() {
        this.modelLoader = new DAELoader(this.config.THREEJS_CROSS_ORIGIN);
        this.modelLoader.onProgress = this.onModelLoaderProgess;
        this.modelLoader.onLoaded = this.onModelLoaderLoaded;
        this.modelLoader.onError = this.onModelLoaderError;
        this.modelLoader.load(this.modelUrl);
    }
    renderModel() {
        this.loadingIndicator.off();
        this.modelRenderer = new ModelRenderer(this.translations);
        this.modelRenderer.render(this.modelContainer, this.model, this.config.THREEJS_SCENE_BACKGROUND_COLOR);
        this.assetViewerView.eventBus.dispatch(new ViewerStartViewing3dModelEvent({ assetViewerView: AssetViewerViewMapper.mapToPublicAssetViewerView(this.assetViewerView) }));
    }
    destroyModel() {
        if (this.buttonExitFullscreen) {
            this.buttonExitFullscreen.off();
        }
        if (this.loadDependenciesPromise) {
            this.loadDependenciesPromise.cancel();
        }
        if (this.modelLoader) {
            this.modelLoader.destroy();
            this.modelLoader = null;
        }
        if (this.modelRenderer) {
            this.modelRenderer.destroy();
            this.modelRenderer = null;
        }
        if (this.modelContainer && this.modelContainer.parentNode) {
            this.modelContainer.parentNode.removeChild(this.modelContainer);
        }
    }
    resize() {
        if (this.modelRenderer) {
            this.modelRenderer.resize();
        }
    }
    destroy() {
        this.removeEventListeners();
        this.destroyModel();
        if (this.manifestXhr) {
            this.manifestXhr.abort();
            this.manifestXhr = null;
        }
        if (this.previewImage) {
            this.previewImage.onload = null;
            this.previewImage.onerror = null;
            this.previewImage.src = TRANSPARENT_IMAGE_SRC;
            this.previewImage = null;
        }
        if (this.buttonView) {
            this.buttonView.destroy();
        }
        if (this.errorMessage) {
            this.errorMessage.destroy();
        }
        if (this.buttonExitFullscreen) {
            this.buttonExitFullscreen.destroy();
        }
        if (this.loadingIndicator) {
            this.loadingIndicator.destroy();
        }
        if (this.loadDependenciesPromise) {
            this.loadDependenciesPromise.cancel();
        }
        if (this.container) {
            this.container.innerHTML = '';
            this.container = null;
        }
    }
    destroyFinal() {
        this.destroy();
    }
}
export class ThreeJsPresentationRendererUnsupportedMessage {
    constructor(container, text) {
        this.htmlElement = null;
        this.className = 'sv-threejs-presentation-renderer__unsupported-message';
        this.htmlElement = document.createElement('div');
        this.htmlElement.className = this.className;
        this.htmlElement.innerHTML = `<div class="${this.className}__container">
            <div class="${this.className}__content">
                <svg viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg"><path d="M512 0C229.2 0 0 229.2 0 512s229.2 512 512 512 512-229.2 512-512S794.8 0 512 0zm-3 184.5c19.9 0 36 16.1 36 36s-16.1 36-36 36-36-16.1-36-36 16.1-36 36-36zm157.2 557.1c0 19.9-16.1 36-36 36H387.8c-19.9 0-36-16.1-36-36s16.1-36 36-36H473V391.5h-60c-19.9 0-36-16.1-36-36s16.1-36 36-36h96c19.9 0 36 16.1 36 36v350.1h85.2c19.9 0 36 16.1 36 36z"/></svg>${text}
            </div>
        </div>`;
        container.appendChild(this.htmlElement);
    }
}
export class ThreeJsPresentationRendererButtonView {
    constructor(container, translations) {
        this.translations = translations;
        this.container = null;
        this.htmlElement = null;
        this.className = 'sv-threejs-presentation-renderer__button-view';
        this.isOn = false;
        this.container = container;
        this.translations = translations;
    }
    on() {
        if (this.isOn) {
            return;
        }
        this.isOn = true;
        this.htmlElement = document.createElement('button');
        this.htmlElement.className = this.className;
        this.htmlElement.onclick = () => {
            this.onclick();
        };
        this.htmlElement.innerHTML = `<div class="${this.className}__container"><svg width="20" height="12" xmlns="http://www.w3.org/2000/svg"><path d="M12 6a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm8 0c0 .79-.43.97-1.903 2.38C15.36 11 13.014 11.994 10 12c-3.014-.006-5.361-1-8.097-3.62C.43 6.97 0 6.79 0 6c0-.79.43-.97 1.903-2.38C4.64 1 6.986.006 10 0c3.014.006 5.361 1 8.097 3.62C19.57 5.03 20 5.21 20 6zm-6 .5C14 4.015 12.21 2 10 2S6 4.015 6 6.5 7.79 11 10 11s4-2.015 4-4.5z" fill="#4D4D4D" fill-rule="evenodd"/></svg><span>${this.translations[TranslationKeys.ThreeJsViewIn3d]}</span></div>`;
        this.container.appendChild(this.htmlElement);
    }
    off() {
        if (!this.isOn) {
            return;
        }
        this.isOn = false;
        this.htmlElement.parentNode.removeChild(this.htmlElement);
    }
    destroy() {
        this.off();
    }
}
export class ThreeJsPresentationRendererButtonExitFullscreen {
    constructor(container) {
        this.container = null;
        this.htmlElement = null;
        this.className = 'sv-threejs-presentation-renderer__button-exit-fullscreen';
        this.isOn = false;
        this.container = container;
    }
    on() {
        if (this.isOn) {
            return;
        }
        this.isOn = true;
        this.htmlElement = document.createElement('button');
        this.htmlElement.className = this.className;
        this.htmlElement.onclick = () => {
            this.onclick();
        };
        this.htmlElement.innerHTML = `<svg width="16" height="16" xmlns="http://www.w3.org/2000/svg"><path fill="#BFBFBF" d="M10.361 7.985l5.173 5.355-2.639 2.629-5.128-5.302L2.64 15.97 0 13.34l5.173-5.355L0 2.637 2.648 0l5.12 5.3L12.885 0l2.648 2.637z"/></g></svg>`;
        this.container.appendChild(this.htmlElement);
    }
    off() {
        if (!this.isOn) {
            return;
        }
        this.isOn = false;
        this.htmlElement.parentNode.removeChild(this.htmlElement);
    }
    destroy() {
        this.off();
    }
}
export class ThreeJsPresentationRendererLoadingIndicator {
    constructor(container, translations, size = 26, strokeWidth = 4) {
        this.translations = translations;
        this.container = null;
        this.htmlElement = null;
        this.className = 'sv-threejs-presentation-renderer__loading-indicator';
        this.size = null;
        this.strokeWidth = null;
        this.radius = null;
        this.dashArray = null;
        this.percent = null;
        this.percentLinear = null;
        this.isOn = false;
        this.requestAnimationFrameId = null;
        this.onComplete = null;
        this.container = container;
        this.translations = translations;
        this.size = size;
        this.strokeWidth = strokeWidth;
        this.radius = this.size / 2 - this.strokeWidth / 2;
        this.dashArray = 2 * Math.PI * this.radius;
        this.render = this.render.bind(this);
    }
    on(percent = 0) {
        if (this.isOn) {
            return;
        }
        this.isOn = true;
        this.percent = this.percentLinear = percent;
        this.htmlElement = document.createElement('div');
        this.htmlElement.className = this.className;
        this.htmlElement.innerHTML = `<div class="${this.className}__content">
            <svg data-scope="progress"
              width="${this.size}"
              height="${this.size}"
              viewBox="0 0 ${this.size} ${this.size}">
              <circle
                cx="${this.size / 2}"
                cy="${this.size / 2}"
                r="${this.radius}"
                fill="none"
                stroke="#5f6061"
                stroke-width="${this.strokeWidth}"
              />
              <circle
                data-scope="progressValue"
                cx="${this.size / 2}"
                cy="${this.size / 2}"
                r="${this.radius}"
                fill="none"
                stroke="#00bb8b"
                stroke-width="${this.strokeWidth}"
                stroke-dasharray="${this.dashArray}"
                stroke-dashoffset="${this.dashArray}"
              />
            </svg>
            <span>${this.translations[TranslationKeys.ThreeJsLoadingModel]}</span>
          </div>`;
        this.container.appendChild(this.htmlElement);
        this.render();
    }
    update(percent = null) {
        this.percent = percent;
    }
    render() {
        this.requestAnimationFrameId = requestAnimationFrame(this.render);
        if (this.percentLinear < this.percent) {
            this.percentLinear = Math.min(this.percentLinear + 1, this.percent);
        }
        const strokeDashoffset = mapRange(this.percentLinear, 0, 100, this.dashArray, 0);
        const progressValue = this.htmlElement.querySelector(`[data-scope='progressValue']`);
        progressValue.style.strokeDashoffset = String(strokeDashoffset);
        if (this.percentLinear === 100) {
            cancelAnimationFrame(this.requestAnimationFrameId);
            this.onComplete();
        }
    }
    off() {
        if (!this.isOn) {
            return;
        }
        this.isOn = false;
        cancelAnimationFrame(this.requestAnimationFrameId);
        this.htmlElement.parentNode.removeChild(this.htmlElement);
    }
    destroy() {
        this.off();
    }
}
export class ThreeJsPresentationRendererErrorMessage {
    constructor(container, translations) {
        this.translations = translations;
        this.container = null;
        this.htmlElement = null;
        this.className = 'sv-threejs-presentation-renderer__error-message';
        this.isOn = false;
        this.container = container;
        this.translations = translations;
    }
    on() {
        if (this.isOn) {
            return;
        }
        this.isOn = true;
        this.htmlElement = document.createElement('div');
        this.htmlElement.className = this.className;
        this.container.appendChild(this.htmlElement);
        this.htmlElement.innerHTML = `<div class="${this.className}__anchor" data-scope="anchor">
        </div><div class="${this.className}__container">
          <div class="${this.className}__content">
          <svg class="icon" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg"><path d="M512 0C229.2 0 0 229.2 0 512s229.2 512 512 512 512-229.2 512-512S794.8 0 512 0zm-40 231c0-22.1 17.9-40 40-40s40 17.9 40 40v369c0 22.1-17.9 40-40 40s-40-17.9-40-40V231zm40 563c-24.9 0-45-20.1-45-45s20.1-45 45-45 45 20.1 45 45c0 24.8-20.1 45-45 45z"></path></svg>
  <button class="close" data-scope="buttonClose"><svg class="close" width="8" height="8" xmlns="http://www.w3.org/2000/svg"><g fill="none" fill-rule="evenodd"><path fill="none" d="M0 0h8v8H0z"/><path fill="#000" d="M5.306 3.993L7.893 6.67l-1.32 1.314-2.564-2.65-2.564 2.65L.125 6.67l2.587-2.677L.125 1.319 1.449 0l2.56 2.65L6.569 0l1.324 1.319z"/></g></svg></button>
          <span>${this.translations[TranslationKeys.ThreeJsRenderErrorTitle]}</span>
          <p>${this.translations[TranslationKeys.ThreeJsRenderErrorBody]}</p>
          </div>
        </div>`;
        this.container.appendChild(this.htmlElement);
        const buttonClose = this.htmlElement.querySelector(`[data-scope='buttonClose']`);
        buttonClose.onclick = () => {
            this.off();
        };
        const anchor = this.htmlElement.querySelector(`[data-scope='anchor']`);
        anchor.scrollIntoView();
    }
    off() {
        if (!this.isOn) {
            return;
        }
        this.isOn = false;
        this.htmlElement.parentNode.removeChild(this.htmlElement);
    }
    destroy() {
        this.off();
    }
}
export class ThreeJsPresentationRendererPreview {
    constructor(container, previewUrl = null) {
        this.htmlElement = null;
        this.className = 'sv-threejs-presentation-renderer__preview';
        this.htmlElement = document.createElement('div');
        this.htmlElement.className = this.className;
        if (previewUrl) {
            this.htmlElement.style.backgroundImage = `url('${previewUrl}')`;
            this.htmlElement.style.backgroundSize = `contain`;
        }
        container.appendChild(this.htmlElement);
        this.htmlElement.onclick = () => {
            if (this.onclick) {
                this.onclick();
            }
        };
    }
}
//# sourceMappingURL=ThreeJsPresentationRenderer.js.map